<script setup lang="ts">
import { ref } from "vue";
import type {
  ChartViewTypes,
  IChartMode
} from "../../common/types/IRTImpactChart";
import type { IHistogramsGroup } from "~/types/IImpactV3Histograms";
import { numberCommaSeparator } from "~/helpers";
import RTImpactControls from "../RTImpactControls/RTImpactControls.vue";
import RTImpactCharts from "../RTImpactCharts/RTImpactCharts.vue";
import { ChartPeriodEnum } from "~/enums";
import RTImpactMap from "../RTImpactMap/RTImpactMap.vue";
import type { IRTImpactContainer } from "./IRTImpactContainer";
import { Checkbox } from "@frontiers-vue3/public-pages";
import { useImpactStore } from "~/stores/impact";

const props = withDefaults(defineProps<IRTImpactContainer>(), {
  initialChartMode: "spline"
});

const impactStore = useImpactStore();
const { sinceBeginningArticleViewText, sinceBeginningTotalViewText } =
  useRuntimeConfig().public;

const chartPeriod = ref<ChartPeriodEnum>(ChartPeriodEnum.beginning);
const chartMode = ref<IChartMode>(props.initialChartMode);
const mapLoaded = ref<boolean>(props.initialChartMode === "map");

const chartSeries = ref<Record<keyof ChartViewTypes, boolean>>({
  totalViews: true,
  articleDownloads: false,
  articleViews: false,
  topicViews: false
});

const chartData = computed((): IHistogramsGroup | undefined => {
  return impactStore.getHistogramsInfo().get(chartPeriod.value);
});

watch(
  () => chartMode.value,
  () => {
    if (!mapLoaded.value && chartMode.value === "map") mapLoaded.value = true;
  }
);
</script>

<template>
  <div class="RTImpactContainer">
    <div v-if="chartData" class="RTImpactContainer__title">
      <p>{{ sinceBeginningTotalViewText }}</p>
      <strong>{{ numberCommaSeparator(chartData.totalViewsCount) }}</strong>
    </div>

    <RTImpactControls
      class="RTImpactContainer__controls"
      :chart-mode="chartMode"
      :chart-period="chartPeriod"
      @chart-mode-updated="(m) => (chartMode = m)"
      @chart-time-period-updated="(t) => (chartPeriod = t)" />

    <template v-if="chartMode !== 'map'">
      <RTImpactCharts
        :chart-mode="chartMode"
        :chart-data="chartData"
        :chart-period="chartPeriod"
        :chart-series-enabled="chartSeries" />

      <ul v-if="chartData" class="RTImpactContainer__list">
        <li v-if="sinceBeginningTotalViewText">
          <Checkbox v-model="chartSeries.totalViews" />
          <div class="colorBox colorBox--blue"></div>
          <strong>{{ numberCommaSeparator(chartData.totalViewsCount) }}</strong>
          <span>{{ sinceBeginningTotalViewText }}</span>
        </li>
        <li>
          <Checkbox v-model="chartSeries.topicViews" />
          <div class="colorBox colorBox--green"></div>
          <strong>{{ numberCommaSeparator(chartData.topicViewsCount) }}</strong>
          <span>Topic views</span>
        </li>
        <li v-if="sinceBeginningArticleViewText">
          <Checkbox v-model="chartSeries.articleViews" />
          <div class="colorBox colorBox--orange"></div>
          <strong>{{
            numberCommaSeparator(chartData.articleViewsCount)
          }}</strong>
          <span>{{ sinceBeginningArticleViewText }}</span>
        </li>
        <li>
          <Checkbox v-model="chartSeries.articleDownloads" />
          <div class="colorBox colorBox--purple"></div>
          <strong>{{
            numberCommaSeparator(chartData.articleDownloadsCount)
          }}</strong>
          <span>Article downloads</span>
        </li>
      </ul>
    </template>
    <template v-if="mapLoaded">
      <RTImpactMap
        v-show="chartMode === 'map'"
        :demographics-errors="demographicsErrors"
        :demographics-loading="demographicsLoading" />
    </template>
  </div>
</template>
<style lang="scss">
.RTImpactContainer {
  display: flex;
  flex-direction: column;
  background-color: $white;
  padding: 32px 24px;
  border: 1px solid $grey20;
  border-radius: 8px;
  min-height: 686px;

  @media #{$break-sm} {
    overflow: auto;
    white-space: nowrap;
  }

  &__title {
    @include f-16-500;

    @media #{$break-xs} {
      position: sticky;
      left: 0;
    }

    p {
      font-weight: inherit;
      margin: 0;
    }

    strong {
      @include f-24-700;
    }
  }

  &__controls {
    margin: 16px 0;

    @media #{$break-xs} {
      position: sticky;
      left: 0;
      z-index: 2;
    }

    & + .RTImpactControls {
      display: flex;
      flex-direction: column;
      flex: 1;
      justify-content: center;

      @media #{$break-xs} {
        &:not(.RTImpactMap, .RTImpactCharts--noData) {
          min-width: 600px;
        }
      }
    }
  }

  &__list {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    column-gap: 24px;
    row-gap: 24px;
    margin-top: 24px;

    @media #{$break-sm} {
      justify-content: space-between;
    }

    @media #{$break-xs} {
      position: sticky;
      left: 0;
    }

    li {
      @include f-14-300;
      display: flex;
      align-items: center;

      .colorBox {
        height: 16px;
        width: 16px;
        border-radius: 2px;
        margin-right: 8px;

        &--blue {
          background-color: $blue50;
        }

        &--green {
          background-color: $green50;
        }

        &--orange {
          background-color: $orange50;
        }

        &--purple {
          background-color: $purple50;
        }
      }

      strong {
        font-weight: 600;
        margin-right: 4px;
      }
    }
  }
}
</style>
